/* global runtime */
// Saldo-kortet og rådgiverens salgsmodell.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.
// While the journal loads, the read model answers the home figures (speed track S3): the
// dashboard's VAT return and the balances card's trial balance are month-aligned and undimensioned.
const homeLedgerReadable = (state, year) => window.PlummeAccountingBasisCoverage.isLoading(state?.accountingBasisCoverage) && Boolean(ledgerBalancesFor(state, year, {}));
const getHomeDashboardModel = () => {
  const state = mergedRuntimeState();
  if (window.PlummeAccountingBasisCoverage.isHydratedStateBlocked(state, { allowImportedStatistics: true }) && !homeLedgerReadable(state, Number(state.activeYear))) {
    return window.PlummeAccountingBasisCoverage.blockedReadModel('home_dashboard', state.accountingBasisCoverage);
  }
  return cachedDerivedModel('homeDashboard', buildHomeDashboardModel);
};

// Saldo-kortet pa Hjem. Saldoen leses fra saldobalansen (utgaende saldo per konto),
// ikke fra en egen teller, sa tallet er det samme som rapportene og bankavstemmingen
// forholder seg til. Kortet bygges for seg og ikke som del av `buildHomeDashboardModel`,
// fordi saldobalansen er den dyreste delen av arsrapporten — Hjem skal ikke betale for
// den nar kortet ikke vises.
const homeBalanceAccountKind = (account) => {
  const kind = String(account?.kind || '').toLowerCase();
  if (kind === 'private') return 'private';
  if (kind === 'cash') return 'cash';
  if (kind === 'card' || kind === 'company_card' || kind === 'payment_solution') return 'card';
  return 'bank';
};

// Driftskontoen og kontantkassa er alltid med og kan ikke fjernes fra kortet — de er
// motkontoene alt oppgjor gar mot, sa et regnskap uten dem finnes ikke.
//
// Hvilken kode de har er derimot ikke gitt. Landets standard (NO 1920/1900, SE
// 1930/1910) gjelder bare til noe annet er bestemt: etter en import fra Fiken ligger
// driftskontoen pa `1920:10001`, og andre systemer gir andre koder. A lase 1920 her
// ville gitt en evig 0-rad ved siden av kontoen pengene faktisk star pa.
//
// Foretakets egen standard bankkonto er den som er merket `invoiceDefault` — samme
// konto oppgjor og fakturaer bruker. `settings.bankAccountingAccount` er ikke brukt
// her, for `normalizeBusinessBankSettings` faller tilbake pa 1920 for alle land.
// Uten registrerte kontoer loser skyggekartet landets standardkode.
const homeBalanceFixedAccounts = (state) => {
  const defaults = getPaymentSettlementDefaults(state);
  const shadowed = chartShadowedCodeMap(state);
  const resolve = (code) => shadowed.get(String(code || '').trim()) || String(code || '').trim();
  const activeBankAccounts = safeArray(state.settings?.bankAccounts)
    .filter((account) => account.active !== false && account.kind !== 'private');
  const defaultBankAccount = activeBankAccounts.find((account) => account.invoiceDefault) || activeBankAccounts[0] || null;
  const bank = defaultBankAccount
    ? getLedgerAccountCode(defaultBankAccount, resolve(defaults.company))
    : resolve(defaults.company);
  return [{ code: bank, kind: 'bank' }, { code: resolve(defaults.cash), kind: 'cash' }]
    .filter((entry) => entry.code)
    .filter((entry, index, list) => list.findIndex((other) => other.code === entry.code) === index);
};
const homeBalanceFixedCodes = (state) => homeBalanceFixedAccounts(state).map((entry) => entry.code);

// Flere betalingskontoer kan peke pa samme hovedbokskonto (Vipps og driftskonto pa
// samme 1920). Saldoen finnes bare per hovedbokskonto, sa radene grupperes pa kode —
// ellers ville totalen talt den samme saldoen en gang per betalingsmate.
const homeBalanceAccountEntries = (state, extraCodes = []) => {
  const defaults = getPaymentSettlementDefaults(state);
  const entries = [];
  const add = (account, kind) => {
    const fallback = kind === 'private' ? defaults.private : kind === 'cash' ? defaults.cash : defaults.company;
    const code = getLedgerAccountCode(account, fallback);
    if (!code) return;
    entries.push({ code, kind, name: String(account?.name || account?.provider || '').trim() });
  };
  homeBalanceFixedAccounts(state).forEach((entry) => add({ ledgerAccountCode: entry.code }, entry.kind));
  safeArray(state.settings?.bankAccounts)
    .filter((account) => account.active !== false)
    .forEach((account) => add(account, homeBalanceAccountKind(account)));
  safeArray(state.settings?.paymentAccounts)
    .filter((account) => account.active !== false)
    .forEach((account) => add(account, homeBalanceAccountKind(account)));
  if (state.settings?.bankAccount) {
    add({
      name: '',
      accountNumber: state.settings.bankAccount,
      ledgerAccountCode: state.settings.bankAccountingAccount || defaults.company,
    }, 'bank');
  }
  // Kontoer brukeren selv har sokt opp og lagt til. De har ingen betalingskonto bak
  // seg, bare en plass i kontoplanen, sa navnet kommer derfra.
  safeArray(extraCodes).forEach((code) => entries.push({ code: String(code || '').trim(), kind: 'ledger', name: '' }));
  return entries.filter((entry) => entry.code);
};

const buildHomeBalancesModel = (extraCodes, hiddenCodes) => {
  if (!runtime.appState || (!runtime.configured && !runtime.user)) return null;
  const state = mergedRuntimeState();
  const year = Number(state.activeYear) || new Date().getFullYear();
  if (window.PlummeAccountingBasisCoverage.isHydratedStateBlocked(state, { allowImportedStatistics: true }) && !homeLedgerReadable(state, year)) {
    return { ...window.PlummeAccountingBasisCoverage.blockedReadModel('home_balances', state.accountingBasisCoverage), year, accounts: null };
  }
  const balances = new Map(trialAccountRows(cachedYearReports(state, year)).map((row) => [row.code, row.amount]));
  const fixed = new Set(homeBalanceFixedCodes(state));
  const hidden = new Set(safeArray(hiddenCodes).filter((code) => !fixed.has(code)));
  // En betalingskonto kan fortsatt peke pa standardkoden etter en import som flyttet
  // kontoen til en variant. Saldobalansen kjenner ikke standardkoden lenger, sa raden
  // ville statt pa 0 for alltid ved siden av den kontoen pengene star pa.
  const shadowedStandardCodes = new Set(chartShadowedCodeMap(state).keys());
  const grouped = new Map();
  homeBalanceAccountEntries(state, extraCodes).forEach((entry) => {
    if (hidden.has(entry.code) || shadowedStandardCodes.has(entry.code)) return;
    const existing = grouped.get(entry.code);
    if (!existing) {
      grouped.set(entry.code, { ...entry, names: entry.name ? [entry.name] : [] });
      return;
    }
    if (entry.name && !existing.names.includes(entry.name)) existing.names.push(entry.name);
    // En privatkonto og en bedriftskonto skal ikke ende pa samme hovedbokskonto, men
    // om det skjer er det bedriftsraden som er den riktige merkelappen.
    if (existing.kind === 'private' && entry.kind !== 'private') existing.kind = entry.kind;
    if (existing.kind === 'ledger' && entry.kind !== 'ledger') existing.kind = entry.kind;
  });
  const accounts = [...grouped.values()]
    .map((entry) => ({
      code: entry.code,
      kind: entry.kind,
      name: entry.names.length ? entry.names.join(' / ') : trialAccountName(state, entry.code),
      balance: num(balances.get(entry.code)),
      fixed: fixed.has(entry.code),
    }))
    .sort((a, b) => a.code.localeCompare(b.code, 'nb-NO'));
  return { year, accounts };
};

const homeBalancePreference = (key) => {
  const stored = runtime.userProfile?.preferences?.[key];
  return Array.isArray(stored) ? stored.map((code) => String(code || '').trim()).filter(Boolean) : null;
};

// Kontoene og saldoene avledes av foretakets state, men listen avhenger ogsa av hva
// brukeren har lagt til og fjernet. Preferansene er derfor del av cachenokkelen — de
// endrer seg uavhengig av state, og en cache pa state alene ville vist forrige liste.
const getHomeBalancesModel = () => {
  const extra = homeBalancePreference('homeBalanceExtraAccounts') || [];
  const removed = homeBalancePreference('homeBalanceHiddenAccounts') || [];
  const model = cachedDerivedModel(
    `homeBalances:${extra.join(',')}:${removed.join(',')}`,
    () => buildHomeBalancesModel(extra, removed),
  );
  if (!model) return null;
  if (model.blocked) return model;
  const stored = homeBalancePreference('homeBalanceAccounts');
  const known = new Set(model.accounts.map((account) => account.code));
  // Uten lagret utvalg vises bedriftens egne kontoer. Privatkonto og mellomregning er
  // sjelden det brukeren vil se pa forsiden, men kan slas pa. Et tomt lagret utvalg er
  // noe brukeren kan velge, og skilles derfor fra «ingen preferanse lagret».
  const selected = stored
    ? stored.filter((code) => known.has(code))
    : model.accounts.filter((account) => account.kind !== 'private').map((account) => account.code);
  return { ...model, selected, hasPreference: Boolean(stored) };
};

// Kontoplanen brukeren kan soke i for a legge til en rad. Bare balansekontoer: et
// saldokort som viser en resultatkonto ville vist arets bevegelse, ikke en beholdning.
const getHomeBalanceAccountOptions = (query = '') => {
  const text = String(query || '').trim().toLowerCase();
  if (!text) return [];
  const shown = new Set((getHomeBalancesModel()?.accounts || []).map((account) => account.code));
  return getChartAccountsModel()
    .filter((account) => account.active !== false)
    .map((account) => ({
      code: String(account.code || account.accountNumber || '').trim(),
      name: String(account.name || '').trim(),
      category: account.category || chartAccountCategory(account.code || account.accountNumber),
    }))
    .filter((account) => account.code && !shown.has(account.code))
    .filter((account) => account.category === 'asset' || account.category === 'equity-liability')
    .filter((account) => `${account.code} ${account.name}`.toLowerCase().includes(text))
    .slice(0, 8);
};

// Valgene lagres pa brukerprofilen, ikke pa foretaket: to personer i samme regnskap kan
// ville folge med pa hver sine kontoer.
const normalizeHomeBalanceCodes = (codes) =>
  [...new Set(safeArray(codes).map((code) => String(code || '').trim()).filter(Boolean))];

const setHomeBalanceAccounts = async (codes) =>
  updateUserPreferencePatch({ homeBalanceAccounts: normalizeHomeBalanceCodes(codes) });

// Legg til en konto fra kontoplanen. Den skal vises med en gang, og et tidligere
// «fjernet» merke pa samme konto maa vekk — ellers ville raden blitt filtrert bort igjen.
const addHomeBalanceAccount = async (code) => {
  const clean = String(code || '').trim();
  if (!clean) return null;
  const model = getHomeBalancesModel();
  const extra = homeBalancePreference('homeBalanceExtraAccounts') || [];
  const hidden = homeBalancePreference('homeBalanceHiddenAccounts') || [];
  const selected = model ? model.selected : [];
  return updateUserPreferencePatch({
    homeBalanceExtraAccounts: normalizeHomeBalanceCodes([...extra, clean]),
    homeBalanceHiddenAccounts: hidden.filter((entry) => entry !== clean),
    homeBalanceAccounts: normalizeHomeBalanceCodes([...selected, clean]),
  });
};

// Fjern en konto fra kortet. Dette rorer ikke kontoplanen eller betalingskontoene i
// innstillingene — bare hva dette kortet viser for denne brukeren. Faste kontoer kan
// bare skjules med avkryssingsboksen, ikke fjernes.
const removeHomeBalanceAccount = async (code) => {
  const clean = String(code || '').trim();
  if (!clean) return null;
  const model = getHomeBalancesModel();
  if (!model || model.accounts.find((account) => account.code === clean)?.fixed) return null;
  const extra = homeBalancePreference('homeBalanceExtraAccounts') || [];
  const hidden = homeBalancePreference('homeBalanceHiddenAccounts') || [];
  return updateUserPreferencePatch({
    homeBalanceExtraAccounts: extra.filter((entry) => entry !== clean),
    homeBalanceHiddenAccounts: normalizeHomeBalanceCodes([...hidden, clean]),
    homeBalanceAccounts: normalizeHomeBalanceCodes(model.selected.filter((entry) => entry !== clean)),
  });
};

const aiAdvisorCustomerConcentration = (state, year, income) => {
  const totals = new Map();
  safeArray(state.invoices).forEach((invoice) => {
    if (!isPostedInvoice(invoice, state) || !reportDateInRange(invoice.date, year)) return;
    const key = invoice.customer?.id || invoice.customer?.name || invoice.customerName || 'Ukjent kunde';
    totals.set(key, (totals.get(key) || 0) + invoiceNet(invoice) * (invoice.kind === 'credit' ? -1 : 1));
  });
  const top = Math.max(0, ...totals.values());
  const total = [...totals.values()].reduce((sum, value) => sum + value, 0);
  return total > 0 && top > 0 ? Math.round(top / total * 1000) / 10 : null;
};
const aiAdvisorImportedPartyInsights = (state, year, report) => {
  const yearly = state.accountingImportReportStatsByYear?.[String(year)]?.parties;
  const source = yearly || state.accountingImportPartyStats || {};
  const scope = yearly ? 'selected-year' : 'imported-archive';
  const compact = (rows, amountKey, total, shareBasis) => {
    const normalized = safeArray(rows).map((entry) => ({
      name: String(entry?.partyName || entry?.name || '').trim().slice(0, 120) || 'Ukjent',
      amount: Math.max(0, num(entry?.[amountKey])),
      outstanding: Math.max(0, num(entry?.outstanding)),
    })).filter((entry) => entry.amount > 0.005 || entry.outstanding > 0.005);
    const linkedTotal = normalized.reduce((sum, entry) => sum + entry.amount, 0);
    const denominator = yearly ? Math.max(0, num(total)) : linkedTotal;
    return normalized.sort((a, b) => b.amount - a.amount || b.outstanding - a.outstanding).slice(0, 5).map((entry) => ({
      ...entry,
      amount: Math.round(entry.amount),
      outstanding: Math.round(entry.outstanding),
      sharePct: denominator > 0 ? Math.round(entry.amount / denominator * 1000) / 10 : null,
      shareBasis: yearly ? shareBasis : 'linkedImportTotal',
    }));
  };
  const customers = compact(source.customers, 'totalSales', report.inntekter, 'yearIncome');
  const suppliers = compact(source.suppliers, 'totalPurchases', report.kostnader, 'yearCosts');
  return { scope: customers.length || suppliers.length ? scope : 'none', limitedToTop: 5, customers, suppliers };
};
const aiAdvisorPreviousYearAvailable = (state, year, previousReport = {}) => Boolean(
  state.accountingImportReportStatsByYear?.[String(year - 1)]
) || Boolean(
  state.accountingImportSalesHistorySummary?.years?.[String(year - 1)]
) || Boolean(
  num(previousReport.inntekter) || num(previousReport.kostnader) || num(previousReport.resultat)
);
// This model is document history, not proof of complete ledger revenue. It may
// remain available while journal coverage is incomplete, but every consumer
// receives the explicit basis and must not relabel it as accounting totals.
const getAiAdvisorSalesModel = (options = {}) => {
  if (!runtime.appState || (!runtime.configured && !runtime.user)) return null;
  if (!globalThis.PlummeAiAdvisorSales) return null;
  const state = mergedRuntimeState();
  const year = Number(options.year || state.activeYear) || new Date().getFullYear();
  return cachedDerivedModel(`aiAdvisorSales:${year}`, () => buildAiAdvisorSalesModelForYear(state, year));
};
const buildAiAdvisorSalesModelForYear = (state, year, options = {}) => {
  const salesInvoicesFor = (targetYear) => safeArray(state.invoices).filter((invoice) =>
    isPostedInvoice(invoice, state)
    && reportDateInRange(invoice.date, targetYear, options));
  // Betalingsdato: siste aktive innbetaling på fakturaen, ellers paidAt hvis den er
  // markert betalt uten registrert betaling. Uten dato teller ikke fakturaen med i
  // snittet for betalingstid i det hele tatt.
  const paidDateOf = (invoice) => {
    const payments = activePaymentsForDocument(state, 'invoice', invoice.id);
    const dates = payments.map((payment) => payment.date).filter(Boolean).sort();
    return dates.length ? dates[dates.length - 1] : (invoice.paidAt || null);
  };
  const recurring = safeArray(state.invoices).filter((invoice) =>
    isPostedInvoice(invoice, state)
    && reportDateInRange(invoice.date, year, options)
    && (invoice.recurring || invoice.subscriptionId));
  const nativeModel = globalThis.PlummeAiAdvisorSales.buildSalesModel({
    year,
    invoices: salesInvoicesFor(year),
    previousInvoices: salesInvoicesFor(year - 1),
    netOf: (invoice) => invoiceNet(invoice) * (invoice.kind === 'credit' ? -1 : 1),
    lineNetOf: invoiceLineNet,
    paidDateOf,
    recurringCount: recurring.filter((invoice) => invoice.kind !== 'credit').length,
    recurringRevenue: recurring.reduce((sum, invoice) => sum + invoiceNet(invoice) * (invoice.kind === 'credit' ? -1 : 1), 0),
  });
  const basis = {
    basis: 'invoice_document_history',
    scope: { year, months: options.months || null },
    accountingTotalsAvailable: !window.PlummeAccountingBasisCoverage.isHydratedStateBlocked(state),
    accountingBasis: state.accountingBasisCoverage || null,
  };
  if (options.months) return { ...nativeModel, ...basis, nativeOnly: true };
  return { ...globalThis.PlummeAiAdvisorSales.mergeImportedSummary(
    nativeModel,
    state.accountingImportSalesHistorySummary?.years?.[String(year)],
    state.accountingImportSalesHistorySummary?.years?.[String(year - 1)],
  ), ...basis };
};
